import React, {FC, ReactNode, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {MobileLanguageSwitcher} from './LanguageSwitcher';
import {NavItem, useNavGroups, useTopNavLinks} from './navData';

/**
 * 移动端菜单。设计稿未提供移动端实现，此文件按 v3 设计语言补齐，导出两个组件：
 *
 * - HamburgerButton：放在玻璃导航的 CTA 区（lg:hidden）。
 * - MobileMenuPanel：全屏抽屉。⚠️ 必须渲染在 <nav> 之外——glass-nav 的 backdrop-filter
 *   会让 nav 成为 fixed 后代的 containing block，若把抽屉放进 nav 内，
 *   fixed inset-0 会塌缩为导航条自身的 64px 高度。
 *
 * 路由变化时由父组件（TopNav）的 pathname effect 调 closeMenu 收起。
 */

interface HamburgerButtonProps {
  onClick: () => void;
}

export const HamburgerButton: FC<HamburgerButtonProps> = ({onClick}) => {
  const {t} = useTranslation('common');
  return (
    <button
      aria-label={t('menu.openAria')}
      className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-slate-600 hover:text-slate-900 transition-colors"
      onClick={onClick}
      type="button"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};

interface PanelProps {
  closeMenu: () => void;
  /** 是否已登录（同域 /user/read 探测，见 useCurrentUser；昵称为空也算已登录） */
  isLoggedIn: boolean;
  onLogin: () => void;
  /** 已登录用户名；已登录但未设置昵称或未登录时为 null */
  currentUserName: string | null;
}

export const MobileMenuPanel: FC<PanelProps> = ({closeMenu, isLoggedIn, onLogin, currentUserName}) => {
  const [openSection, setOpenSection] = useState<number | null>(null);
  const {t} = useTranslation('common');
  const navGroups = useNavGroups();
  const topNavLinks = useTopNavLinks();

  const handleSectionClick = (index: number) => () => {
    setOpenSection(openSection === index ? null : index);
  };

  const renderItem = (item: NavItem): ReactNode => {
    const content = (
      <>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${item.iconBgClass}`}>
          <span className={item.iconColorClass}>{item.icon}</span>
        </div>
        <div>
          <div className="text-sm font-medium text-slate-900">{item.title}</div>
          <div className="text-xs text-slate-500">{item.description}</div>
        </div>
      </>
    );

    if (item.onClick) {
      return (
        <button
          className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-md hover:bg-slate-50 transition-colors"
          key={item.title}
          onClick={() => {
            item.onClick?.();
            closeMenu();
          }}
          type="button"
        >
          {content}
        </button>
      );
    }
    if (item.to) {
      return (
        <Link
          className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-slate-50 transition-colors"
          key={item.title}
          onClick={closeMenu}
          to={item.to}
        >
          {content}
        </Link>
      );
    }
    if (item.href) {
      return (
        <a
          className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-slate-50 transition-colors"
          href={item.href}
          key={item.title}
          onClick={closeMenu}
          rel="noreferrer"
          target="_blank"
        >
          {content}
        </a>
      );
    }
    return (
      <button
        className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-md hover:bg-slate-50 transition-colors cursor-default"
        key={item.title}
        type="button"
      >
        {content}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-test="TopNavMobileMenu__menu">
      {/* 抽屉头部，高度与玻璃导航一致（h-16） */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
        <span className="text-base font-semibold text-slate-900">{t('menu.title')}</span>
        <button
          aria-label={t('menu.closeAria')}
          className="w-10 h-10 flex items-center justify-center rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
          onClick={closeMenu}
          type="button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {/* 分组手风琴 */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {navGroups.map((group, index) => (
          <React.Fragment key={group.label}>
            <div>
              <button
                className="w-full flex items-center justify-between px-2 py-3 text-sm font-medium text-slate-700"
                onClick={handleSectionClick(index)}
                type="button"
              >
                {group.label}
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${openSection === index ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="mt-1 space-y-1">{openSection === index && group.items.map(renderItem)}</div>
            </div>
            {/* 顶层链接（浏览器插件/API对接）：非下拉，紧跟在「产品服务」（首个分组）之后 */}
            {index === 0 &&
              topNavLinks.map((link) =>
                link.to ? (
                  <Link
                    className="block px-2 py-3 text-sm font-medium text-slate-700"
                    key={link.title}
                    onClick={closeMenu}
                    to={link.to}
                  >
                    {link.title}
                  </Link>
                ) : (
                  <a
                    className="block px-2 py-3 text-sm font-medium text-slate-700"
                    href={link.href}
                    key={link.title}
                    onClick={closeMenu}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {link.title}
                  </a>
                ),
              )}
          </React.Fragment>
        ))}
      </div>
      {/* 底部 CTA：问候语仅登录且有昵称时展示；已登录无昵称时不渲染登录行（主按钮已是「进入工作台」） */}
      <div className="p-6 border-t border-slate-100 flex flex-col gap-3">
        <MobileLanguageSwitcher />
        {currentUserName ? (
          <button className="text-sm text-slate-600 font-medium py-2" onClick={onLogin} type="button">
            {t('nav.cta.greeting', {name: currentUserName})}
          </button>
        ) : (
          !isLoggedIn && (
            <button className="text-sm text-slate-600 font-medium py-2" onClick={onLogin} type="button">
              {t('nav.cta.login')}
            </button>
          )
        )}
        <button
          className="btn-primary text-sm font-semibold text-white px-5 py-2.5 rounded-md text-center"
          onClick={onLogin}
          type="button"
        >
          {isLoggedIn ? t('nav.cta.enterWorkspace') : t('nav.cta.freeTrial')}
        </button>
      </div>
    </div>
  );
};
